import * as THREE from "three";
import { GeoBuilder } from "../render/GeoBuilder";
import { RNG } from "../core/RNG";
import { shade, type TileDef } from "./Tiles";
import type { TileMap } from "./TileMap";
import { TILE } from "./constants";

const FLOOR_THICKNESS = 0.2;

/**
 * Half-width of the sun's shadow frustum, in world units. It follows the
 * player, and anything outside it samples the clamped edge of the shadow map
 * and reads as a hard band of shade across the horizon — so it has to reach
 * past the fog.
 */
const SHADOW_EXTENT = 58;

/** Where the sun sits relative to whatever the shadow frustum is centred on. */
const SUN_OFFSET = { x: 26, y: 52, z: 22 };

/**
 * Turns a TileMap into geometry and lights.
 *
 * The whole static map is baked into two meshes (solid terrain and animated
 * liquid) plus one for scenery, so a map costs three draw calls no matter how
 * big it is.
 */
export class MapRenderer {
  readonly object3D = new THREE.Group();

  private terrain: THREE.Mesh | null = null;
  private liquid: THREE.Mesh | null = null;
  private props: THREE.Mesh | null = null;
  private apron: THREE.Mesh | null = null;
  private sun: THREE.DirectionalLight | null = null;
  private time = 0;

  constructor(private map: TileMap) {
    // Geometry is authored in tile units and scaled once, so tile heights and
    // prop sizes stay readable numbers regardless of the world scale.
    this.object3D.scale.setScalar(TILE);
    this.buildGeometry();
  }

  /** Meshes the camera should not be able to see through. */
  get solidMeshes(): THREE.Object3D[] {
    return this.terrain ? [this.terrain] : [];
  }

  private buildGeometry(): void {
    const solid = new GeoBuilder();
    const liquid = new GeoBuilder();
    const props = new GeoBuilder();
    const unitBox = new THREE.BoxGeometry(1, 1, 1);

    this.map.forEach((x, y, tile) => {
      const target = tile.animated ? liquid : solid;
      const height = tile.height ?? 0;
      const top = tile.color;
      const side = tile.sideColor ?? shade(tile.color, 0.32);

      if (height > 0) {
        // A raised block sits on top of a floor slab so there are no gaps at
        // the base when you look at it from a low angle.
        target.addBlock(unitBox, top, side, {
          position: [x, height / 2, y],
          scale: [1, height, 1],
        });
        target.addBlock(unitBox, shade(top, 0.4), side, {
          position: [x, -FLOOR_THICKNESS / 2, y],
          scale: [1, FLOOR_THICKNESS, 1],
        });
      } else {
        target.addBlock(unitBox, top, side, {
          position: [x, -FLOOR_THICKNESS / 2, y],
          scale: [1, FLOOR_THICKNESS, 1],
        });
      }

      if (tile.prop) {
        // Deterministic jitter: the same tile always gets the same tree.
        const local = new RNG((x * 73856093) ^ (y * 19349663));
        buildProp(props, tile, x, y, local);
      }
    });

    unitBox.dispose();

    this.terrain = solid.buildMesh();
    if (this.terrain) this.object3D.add(this.terrain);

    this.liquid = liquid.buildMesh();
    if (this.liquid) {
      this.liquid.castShadow = false;
      this.object3D.add(this.liquid);
    }

    this.props = props.buildMesh();
    if (this.props) this.object3D.add(this.props);

    this.buildApron();
  }

  /**
   * A large plate underneath and around the map, tinted like its most common
   * tile. Without it you can see the void past the edge of the world.
   */
  private buildApron(): void {
    const counts = new Map<number, number>();
    this.map.forEach((_x, _y, tile) => counts.set(tile.color, (counts.get(tile.color) ?? 0) + 1));
    let dominant = 0x000000;
    let best = -1;
    for (const [color, count] of counts) {
      if (count > best) {
        best = count;
        dominant = color;
      }
    }

    const apron = new THREE.Mesh(
      new THREE.PlaneGeometry(200, 200),
      new THREE.MeshLambertMaterial({ color: shade(dominant, 0.35) }),
    );
    apron.rotation.x = -Math.PI / 2;
    apron.position.set(this.map.width / 2, -0.45, this.map.height / 2);
    apron.receiveShadow = false;
    this.apron = apron;
    this.object3D.add(apron);
  }

  /** Adds sky colour, fog and lights to the scene the map is rendered in. */
  applyEnvironment(scene: THREE.Scene): void {
    const def = this.map.def;
    const sky = def.sky ?? 0x7fc4ff;
    scene.background = new THREE.Color(sky);
    if (def.fog) scene.fog = new THREE.Fog(sky, def.fog.near, def.fog.far);

    // A fairly bright ground bounce keeps faces turned away from the sun
    // readable instead of near-black.
    const hemi = new THREE.HemisphereLight(def.ambientColor ?? sky, 0x8a8a96, def.ambient ?? 1.1);
    scene.add(hemi);

    // Fill from the opposite side, casting nothing. Without it every surface
    // facing away from the sun — the inside of the border mountains, the north
    // face of every wall — renders as a black cut-out against the sky.
    const fill = new THREE.DirectionalLight(def.fillColor ?? 0xb9cfff, def.fillIntensity ?? 0.55);
    fill.position.set(-SUN_OFFSET.x, SUN_OFFSET.y * 0.5, -SUN_OFFSET.z);
    scene.add(fill);

    const sun = new THREE.DirectionalLight(def.sunColor ?? 0xfff2d0, def.sunIntensity ?? 1.5);
    sun.position.set(SUN_OFFSET.x, SUN_OFFSET.y, SUN_OFFSET.z);
    sun.castShadow = true;
    // The frustum follows the player (see setFocus). It has to be wide enough
    // to cover everything the camera can see: geometry outside it samples the
    // clamped edge of the shadow map and reads as a hard band of shade.
    sun.shadow.mapSize.set(2048, 2048);
    const cam = sun.shadow.camera;
    cam.near = 0.5;
    cam.far = 160;
    cam.left = -SHADOW_EXTENT;
    cam.right = SHADOW_EXTENT;
    cam.top = SHADOW_EXTENT;
    cam.bottom = -SHADOW_EXTENT;
    // Three never recomputes a shadow camera's projection for you. Without
    // this the light keeps its default 10x10 box and everything past it
    // samples the clamped edge of the shadow map — a hard band of shade across
    // the horizon that looks like fog gone wrong.
    cam.updateProjectionMatrix();
    sun.shadow.bias = -0.002;
    scene.add(sun, sun.target);
    this.sun = sun;
  }

  /**
   * Keeps the shadow frustum centred on the player. Without this a large map
   * either has no shadows nearby or needs an absurd shadow map.
   */
  setFocus(x: number, z: number): void {
    if (!this.sun) return;
    this.sun.position.set(x + SUN_OFFSET.x, SUN_OFFSET.y, z + SUN_OFFSET.z);
    this.sun.target.position.set(x, 0, z);
    this.sun.target.updateMatrixWorld();
  }

  update(dt: number): void {
    this.time += dt;
    if (this.liquid) {
      // Gentle vertical bob reads as water without touching the geometry.
      this.liquid.position.y = Math.sin(this.time * 1.6) * 0.04 - 0.04;
    }
  }

  dispose(): void {
    for (const mesh of [this.terrain, this.liquid, this.props, this.apron]) {
      if (!mesh) continue;
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.object3D.remove(mesh);
    }
    this.terrain = this.liquid = this.props = this.apron = null;
  }
}

// ------------------------------------------------------------------- props

const CYL = new THREE.CylinderGeometry(1, 1, 1, 7);
const CONE = new THREE.ConeGeometry(1, 1, 7);
const BOX = new THREE.BoxGeometry(1, 1, 1);
const ROCK = new THREE.IcosahedronGeometry(1, 0);
const OCTA = new THREE.OctahedronGeometry(1, 0);
const SPHERE = new THREE.SphereGeometry(1, 8, 6);

function buildProp(b: GeoBuilder, tile: TileDef, x: number, y: number, rng: RNG): void {
  const base = tile.height ?? 0;
  const jitterX = x + rng.float(-0.16, 0.16);
  const jitterZ = y + rng.float(-0.16, 0.16);
  const spin = rng.float(0, Math.PI * 2);
  const color = tile.propColor;

  switch (tile.prop) {
    case "tree": {
      const scale = rng.float(0.85, 1.15);
      b.add(CYL, 0x6b4a2a, {
        position: [jitterX, base + 0.3 * scale, jitterZ],
        scale: [0.09, 0.6 * scale, 0.09],
      });
      const leaf = color ?? 0x2f7d3a;
      b.add(SPHERE, leaf, {
        position: [jitterX, base + 0.72 * scale, jitterZ],
        scale: [0.36 * scale, 0.32 * scale, 0.36 * scale],
      });
      b.add(SPHERE, shade(leaf, 0.15), {
        position: [jitterX + 0.12, base + 0.92 * scale, jitterZ - 0.08],
        scale: 0.24 * scale,
      });
      break;
    }
    case "pine": {
      const scale = rng.float(0.9, 1.2);
      b.add(CYL, 0x5a3d22, {
        position: [jitterX, base + 0.2 * scale, jitterZ],
        scale: [0.08, 0.4 * scale, 0.08],
      });
      const leaf = color ?? 0x1f6b3a;
      for (let i = 0; i < 3; i++) {
        const t = i / 3;
        b.add(CONE, shade(leaf, i * 0.07), {
          position: [jitterX, base + (0.45 + i * 0.28) * scale, jitterZ],
          rotation: [0, spin, 0],
          scale: [(0.36 - t * 0.09) * scale, 0.45 * scale, (0.36 - t * 0.09) * scale],
        });
      }
      break;
    }
    case "rock": {
      const scale = rng.float(0.16, 0.28);
      b.add(ROCK, color ?? 0x8a8a94, {
        position: [jitterX, base + scale * 0.7, jitterZ],
        rotation: [rng.float(0, 1), spin, rng.float(0, 1)],
        scale,
      });
      break;
    }
    case "flower": {
      for (let i = 0; i < 3; i++) {
        const fx = x + rng.float(-0.35, 0.35);
        const fz = y + rng.float(-0.35, 0.35);
        b.add(CYL, 0x3f8a3f, { position: [fx, base + 0.07, fz], scale: [0.015, 0.14, 0.015] });
        b.add(SPHERE, color ?? rng.pick([0xff6f91, 0xffd23f, 0xfff2f5]), {
          position: [fx, base + 0.16, fz],
          scale: 0.05,
        });
      }
      break;
    }
    case "sign": {
      b.add(BOX, 0x6b4a2a, { position: [x, base + 0.2, y], scale: [0.06, 0.4, 0.06] });
      b.add(BOX, color ?? 0xb98a4a, { position: [x, base + 0.44, y + 0.02], scale: [0.42, 0.24, 0.05] });
      break;
    }
    case "torch": {
      b.add(BOX, 0x4a3a2a, { position: [x, base + 0.3, y], scale: [0.1, 0.6, 0.1] });
      b.add(SPHERE, color ?? 0xffb03a, { position: [x, base + 0.68, y], scale: [0.12, 0.16, 0.12] });
      break;
    }
    case "grave": {
      b.add(BOX, color ?? 0x9a9aa6, {
        position: [x, base + 0.22, y],
        rotation: [0, rng.float(-0.2, 0.2), 0],
        scale: [0.3, 0.44, 0.08],
      });
      b.add(BOX, color ?? 0x9a9aa6, { position: [x, base + 0.34, y], scale: [0.5, 0.09, 0.07] });
      break;
    }
    case "crystal": {
      const scale = rng.float(0.2, 0.34);
      b.add(OCTA, color ?? 0x66e0ff, {
        position: [jitterX, base + scale, jitterZ],
        rotation: [0, spin, 0],
        scale: [scale * 0.6, scale * 1.6, scale * 0.6],
      });
      break;
    }
  }
}
